import { vw, vh } from './utils/constants.js'
import { Journey } from './journey'

/**
 * Enable the GSAP animation for a specific stage
 * @param {number} stageID - The ID of the stage to initialize
 * @param {Journey} journey - The Journey instance
 *
 * @return {boolean} - True if the stage was initialized, false otherwise
 */
export const initializeStage = (stageID, journey, stageSelectorST) => {
    if (journey.isInitialized(stageID)) {
        // Reset side-step timelines to the initial position
        journey.sideStepTimelines.forEach((timeline) => {
            timeline.seek(0)
        })
        return false
    }

    switch (stageID) {
        case 0:
            return initializeStage0(journey, stageSelectorST)
        case 1:
            return initializeStage1(journey, stageSelectorST)
        case 2:
            return initializeStage2(journey, stageSelectorST)
        case 3:
            return initializeStage3(journey, stageSelectorST)
        case 4:
            return initializeStage4(journey, stageSelectorST)
        case 5:
            return initializeStage5(journey, stageSelectorST)
        case 6:
            return initializeStage6(journey, stageSelectorST)
        case 7:
            return initializeStage7(journey, stageSelectorST)
        case 8:
            return initializeStage8(journey, stageSelectorST)
        case 9:
            return initializeStage9(journey, stageSelectorST)
        case 10:
            return initializeStage10(journey, stageSelectorST)
        case 11:
            return initializeStage11(journey, stageSelectorST)
        default:
            console.warn(`Stage ${stageID} not found`)

            return false
    }
}

const initializeStage0 = (journey, stageSelectorST) => {
    /****************
     *** STAGE 0 ***
     *****************/
    const stage_0 = document.getElementById('stage-0')
    journey.stage = 0

    /*** STEP 0.1 ***/
    journey.step = '0-1'
    const tl_0_1 = gsap.timeline()

    tl_0_1.fromTo(
        '.welcome-text',
        {
            yPercent: -50,
        },
        {
            yPercent: -100,
            scale: 0.9,
            autoAlpha: 0,
        }
    )

    journey.pinWhileAnimating({
        pin: false,
        end: '+=150%',
        onEnter: () => {
            journey.enterStage(0)
        },
        onLeaveBack: () => {
            journey.leaveBackStage(0)
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({
        animation: tl_0_1,
        // start: 'top+=95% bottom',
    })

    /*** STEP 0.2 ***/
    journey.step = '0-2'
    const step_0_2_3 = journey.getStep('0-2_3')
    const tl_0_2 = gsap.timeline()
    journey.addSideStepTimeline(tl_0_2)

    tl_0_2
        .fromTo(
            journey.step.querySelector('.background'),
            {
                autoAlpha: 0,
            },
            {
                autoAlpha: 1,
                duration: 15,
            }
        )
        .to(
            journey.step.querySelector('.layered-image'),
            {
                scale: 1.5,
                x: -25,
                duration: 15,
            },
            '-=15'
        )
        .to(
            journey.step.querySelector('.foreground'),
            {
                autoAlpha: 0,
                duration: 15,
            },
            '-=5'
        )
        .set(step_0_2_3, {
            autoAlpha: 1,
        })
        .fromTo(
            step_0_2_3,
            {
                x: '100vw',
            },
            {
                x: 0,
                duration: 15,
            },
            '+=3'
        )
        .addLabel('side-step-shown')
        .to(
            step_0_2_3.querySelector('.step-text'),
            {
                y: (index, target, targets) => {
                    return -(vh + target.offsetHeight + 64)
                },
                duration: 35,
                ease: 'slow(0.1, 0.4, false)',
            },
            '+=2'
        )
        .to(
            step_0_2_3,
            {
                x: '100vw',
                duration: 20,
            },
            '+=1'
        )

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            journey.pinWhileAnimating({
                end: '+=925%',
                pinSpacing: false,
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({ end: '+=925%' })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({ animation: tl_0_2, end: '+=900%' })
    journey.handleScrollToSide(step_0_2_3, tl_0_2)

    /*** STEP 0.3 ***/
    journey.step = '0-3'
    const tl_0_3 = gsap.timeline()

    tl_0_3
        .from(journey.step.querySelector('.protas'), {
            scale: 0.9,
            duration: 1,
            ease: 'elastic.out(1, 1)',
        })
        .fromTo(
            journey.step.querySelector('.animated-text'),
            {
                autoAlpha: 0,
                yPercent: -50,
                y: 50,
                scale: 0.9,
            },
            {
                autoAlpha: 1,
                y: 0,
                scale: 1,
                duration: 2,
                ease: 'elastic.out(1, 1)',
            },
            '-=0.5'
        )

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            // Previous step
            journey.pinWhileAnimating({
                start: 'top center',
                end: '+=925%',
            })
            // Current step
            journey.pinWhileAnimating({ pinSpacing: false })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({
        animation: tl_0_3,
        scrub: false,
        toggleActions: 'play none none reverse',
    })

    /*** STEP 0.4 ***/
    journey.step = '0-4'
    const step_0_4_5 = journey.getStep('0-4_5')
    const tl_0_4 = gsap.timeline()
    journey.addSideStepTimeline(tl_0_4)

    tl_0_4
        .fromTo(
            journey.step.querySelector('.bus'),
            {
                xPercent: -50,
                y: -10,
                scale: 0.5,
                skewX: 4,
            },
            {
                xPercent: 0,
                y: 0,
                scale: 1,
                skewX: 0,
                ease: 'power2.out',
                duration: 15,
            }
        )
        .from(
            journey.step.querySelector('.oriol'),
            {
                xPercent: -30,
                duration: 15,
            },
            '-=5'
        )
        .set(step_0_4_5, {
            autoAlpha: 1,
        })
        .fromTo(
            step_0_4_5,
            {
                x: '100vw',
            },
            {
                x: 0,
                duration: 20,
            },
            '+=3'
        )
        .addLabel('side-step-shown')
        .to(
            step_0_4_5.querySelector('.step-text'),
            {
                y: (index, target, targets) => {
                    return -(vh + target.offsetHeight + 64)
                },
                duration: 35,
                ease: 'slow(0.1, 0.4, false)',
            },
            '+=2'
        )
        .to(
            step_0_4_5,
            {
                x: '100vw',
                duration: 20,
            },
            '+=1'
        )

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            // Previous step
            journey.pinWhileAnimating({ start: 'top center' })
            // Current step
            journey.pinWhileAnimating({
                end: '+=1000%',
                pinSpacing: false,
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({ end: '+=1000%' })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({ animation: tl_0_4, end: '+=975%' })
    journey.handleScrollToSide(step_0_4_5, tl_0_4)

    /*** STEP 0.5 ***/
    journey.step = '0-5'
    const tl_0_5 = gsap.timeline()

    tl_0_5
        .fromTo(
            journey.step.querySelector('.main'),
            {
                scale: 1.25,
                x: -25,
            },
            {
                scale: 0.75,
            }
        )
        .fromTo(
            journey.step.querySelector('.main'),
            {
                autoAlpha: 1,
            },
            {
                autoAlpha: 0,
            },
            '-=0.25'
        )

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            // Previous step
            journey.pinWhileAnimating({
                start: 'top center',
                end: '+=1000%',
            })
            // Current step
            journey.pinWhileAnimating({
                top: 'top top',
                end: '+=250%',
                onLeave: (self) => {
                    journey.leaveStage(0, self)
                },
                onEnterBack: (self) => {
                    journey.enterBackStage(0, self)
                },
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({
                end: '+=250%',
                onLeave: (self) => {
                    journey.leaveStage(0, self)
                },
                onEnterBack: (self) => {
                    journey.enterBackStage(0, self)
                },
            })
        },
    })
    journey.pinWhileTransitioning() // TODO: This is not being triggered on mobile, causing the stage-selector to appear before expected.
    journey.triggerAnimation({ animation: tl_0_5, end: '+=200%' })
    stageSelectorST = journey.showStageSelector()
    journey.setInitialized(0)
    return true
}

const initializeStage1 = (journey) => {
    /****************
     *** STAGE 1 ***
     *****************/
    journey.stage = 1
    const stage_1 = journey.stage

    /*** STEP 1.1 ***/
    journey.step = '1-1'
    const step_1_1_2 = journey.getStep('1-1_2')
    const tl_1_1 = gsap.timeline()
    journey.addSideStepTimeline(tl_1_1)

    tl_1_1
        .to(journey.step.querySelector('.stage-introduction'), {
            y: (index, target, targets) => -(vh + target.offsetHeight + 64),
            duration: 45,
            ease: 'slow(0.1, 0.4, false)',
        })
        .fromTo(
            journey.step.querySelector('.fullheight-img'),
            {
                autoAlpha: 1,
            },
            {
                autoAlpha: 0,
                duration: 20,
            }
        )
        .fromTo(
            journey.step.querySelector('.layer.cover'),
            {
                autoAlpha: 0,
            },
            {
                autoAlpha: 1,
                duration: 20,
            },
            '<'
        )
        .fromTo(
            journey.step.querySelector('.layer.cover'),
            {
                scale: 1.1,
            },
            {
                scale: 1.2,
                duration: 10,
            },
            '<'
        )
        .fromTo(
            journey.stage.querySelector('.stage-header'),
            {
                autoAlpha: 0,
                y: -50,
            },
            {
                autoAlpha: 1,
                y: 0,
                ease: 'back.out(1.4)',
                duration: 5,
            }
        )
        .fromTo(
            '.side-timeline',
            {
                autoAlpha: 0,
                x: -75,
            },
            {
                autoAlpha: 1,
                x: 0,
                ease: 'back.out(1.4)',
                duration: 5,
            },
            '<'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onLeaveBack
                journey.leaveSideStep(1)
            }
        })
        .set(step_1_1_2, {
            autoAlpha: 1,
        })
        .fromTo(
            step_1_1_2,
            {
                x: '100vw',
            },
            {
                x: 0,
                duration: 20,
            },
            '+=3'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onEnter
                journey.enterSideStep(1, true)
            }
        })
        .to(
            step_1_1_2.querySelector('.step-text'),
            {
                y: (index, target, targets) => {
                    return -(vh + target.offsetHeight + 24) // For some reason, the offsetHeight is 24px smaller when the calculations are made
                },
                duration: 55,
                ease: 'slow(0.1, 0.4, false)',
            },
            '+=2'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onEnterBack
                journey.enterSideStep(1)
            }
        })
        .to(
            step_1_1_2,
            {
                x: '100vw',
                duration: 20,
            },
            '+=1'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onLeave
                journey.leaveSideStep(1)
            }
        }, '-=1')

    journey.pinWhileAnimating({
        end: '+=1350%',
        onEnter: () => {
            journey.enterStage(1)
        },
        onLeaveBack: () => {
            journey.leaveBackStage(1)
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({
        animation: tl_1_1,
        start: 'top top+=1px', // Offset by 1px so it gets triggered simultaneously with pinWhileAnimating
        end: '+=1350%',
    })

    /*** STEP 1.2 ***/
    journey.step = '1-2'
    const tl_1_2 = gsap.timeline()

    tl_1_2
        .from(journey.step.querySelector('.mares'), {
            scale: 0.9,
            duration: 1,
            ease: 'elastic.out(1, 1)',
        })
        .fromTo(
            journey.step.querySelector('.animated-text'),
            {
                autoAlpha: 0,
                yPercent: -50,
                y: 50,
                scale: 0.9,
            },
            {
                autoAlpha: 1,
                y: 0,
                scale: 1,
                duration: 2,
                ease: 'elastic.out(1, 1)',
            },
            '-=0.5'
        )

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            journey.pinWhileAnimating({ pinSpacing: false })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating()
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({
        animation: tl_1_2,
        scrub: false,
        toggleActions: 'play none none reverse',
    })

    /*** STEP 1.3 ***/
    journey.step = '1-3'
    const tl_1_3 = gsap.timeline()

    journey.step.querySelectorAll('.cloud.animated').forEach((cloud) => {
        gsap.set(cloud, {
            x: vw + cloud.offsetWidth,
        })
        gsap.to(
            cloud,
            {
                x: 0,
                x: -cloud.offsetWidth,
                duration: 'random(10, 15)',
                repeat: -1,
                ease: 'linear',
            },
            Math.random() * 7
        )
    })

    tl_1_3.fromTo(
        journey.step.querySelector('.foreground'),
        {
            xPercent: -50,
        },
        {
            xPercent: 0,
            ease: 'slow(0.1, 0.4, false)',
        }
    )

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            // Previous step
            journey.pinWhileAnimating({ start: 'top center' })
            // Current step
            journey.pinWhileAnimating({
                end: '+=325%',
                pinSpacing: false,
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({ end: '+=325%' })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({ animation: tl_1_3, end: '+=300%' })

    /*** STEP 1.4 ***/
    journey.step = '1-4'
    const tl_1_4 = gsap.timeline()
    const bg_1_4 = journey.step.querySelector('.background')

    tl_1_4
        .to(bg_1_4, {
            x: -(bg_1_4.offsetWidth - bg_1_4.offsetWidth / 1.5),
        })
        .fromTo(
            journey.step.querySelector('.bus'),
            {
                xPercent: -5,
            },
            {
                xPercent: 5,
            },
            0
        )
        .fromTo(
            journey.step.querySelector('.bus'),
            {
                yPercent: 5,
            },
            {
                yPercent: 7,
                ease: 'back.out(1)',
            },
            0
        )

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            // Previous step
            journey.pinWhileAnimating({
                start: 'top center',
                end: '+=325%',
            })
            // Current step
            journey.pinWhileAnimating({
                end: '+=300%',
                pinSpacing: false,
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({ end: '+=300%' })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({ animation: tl_1_4, end: '+=275%' })

    /*** STEP 1.5 ***/
    journey.step = '1-5'
    const step_1_5_6 = journey.getStep('1-5_6')
    const tl_1_5 = gsap.timeline()
    journey.addSideStepTimeline(tl_1_5)

    tl_1_5
        .fromTo(
            journey.step.querySelector('.bus'),
            {
                xPercent: -25,
                scale: 0.9,
            },
            {
                xPercent: 5,
                scale: 1,
                ease: 'power2.out',
                duration: 15,
            }
        )
        .fromTo(
            journey.step.querySelector('.marco'),
            {
                autoAlpha: 0,
                scale: 1.25,
            },
            {
                autoAlpha: 1,
                scale: 1,
                duration: 10,
            },
            '-=10'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onLeaveBack
                journey.leaveSideStep(1)
            }
        })
        .set(step_1_5_6, {
            autoAlpha: 1,
        })
        .fromTo(
            step_1_5_6,
            {
                x: '100vw',
            },
            {
                x: 0,
                duration: 15,
            },
            '+=3'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onEnter
                journey.enterSideStep(1)
            }
        })
        .to(
            step_1_5_6.querySelector('.step-text'),
            {
                y: (index, target, targets) => {
                    return -(vh + target.offsetHeight + 48) // For some reason, the offsetHeight is 48px smaller when the calculations are made
                },
                duration: 35,
                ease: 'slow(0.1, 0.4, false)',
            },
            '+=2'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onEnterBack
                journey.enterSideStep(1)
            }
        })
        .to(
            step_1_5_6,
            {
                x: '100vw',
                duration: 20,
            },
            '+=1'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onLeave
                journey.leaveSideStep(1)
            }
        }, '-=1')

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            // Previous step
            journey.pinWhileAnimating({
                start: 'top center',
                end: '+=300%',
            })
            // Current step
            journey.pinWhileAnimating({
                end: '+=1050%',
                pinSpacing: false,
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({ end: '+=1050%' })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({ animation: tl_1_5, end: '+=1025%' })

    /*** STEP 1.6 ***/
    journey.step = '1-6'
    const tl_1_6 = gsap.timeline()

    tl_1_6
        .fromTo(
            journey.step.querySelector('.tranvia'),
            {
                autoAlpha: 0,
            },
            {
                autoAlpha: 1,
            }
        )
        .fromTo(
            journey.step.querySelector('.autobus'),
            {
                autoAlpha: 0,
            },
            {
                autoAlpha: 1,
            }
        )

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            // Previous step
            journey.pinWhileAnimating({
                start: 'top center',
                end: '+=1050%',
            })
            // Current step
            journey.pinWhileAnimating({
                end: '+=150%',
                onLeave: (self) => {
                    journey.leaveStage(1, self)
                },
                onEnterBack: (self) => {
                    journey.enterBackStage(1, self)
                },
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({
                end: '+=150%',
                onLeave: (self) => {
                    journey.leaveStage(1, self)
                },
                onEnterBack: (self) => {
                    journey.enterBackStage(1, self)
                },
            })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({ animation: tl_1_6, end: '+=125%' })
    stageSelectorST = journey.showStageSelector()
    journey.setInitialized(1)
    return true
}

const initializeStage2 = (journey) => {
    /****************
     *** STAGE 2 ***
     *****************/
    journey.stage = 2
    const stage_2 = journey.stage

    /*** STEP 2.1 ***/
    journey.step = '2-1'
    const tl_2_1 = gsap.timeline()

    tl_2_1
        .to(journey.step.querySelector('.stage-introduction'), {
            y: (index, target, targets) => -(vh + target.offsetHeight + 64),
            duration: 50,
            ease: 'slow(0.1, 0.4, false)',
        })
        .fromTo(
            journey.step.querySelector('.fullheight-img'),
            {
                autoAlpha: 1,
            },
            {
                autoAlpha: 0,
                duration: 40,
            }
        )
        .fromTo(
            journey.step.querySelector('.layer.cover'),
            {
                autoAlpha: 0,
            },
            {
                autoAlpha: 1,
                duration: 40,
            },
            '<'
        )
        .fromTo(
            journey.step.querySelector('.layer.cover'),
            {
                scale: 1.1,
            },
            {
                scale: 1.15,
                duration: 20,
            },
            '<'
        )
        .fromTo(
            stage_2.querySelector('.stage-header'),
            {
                autoAlpha: 0,
                y: -50,
            },
            {
                autoAlpha: 1,
                y: 0,
                ease: 'back.out(1.4)',
                duration: 5,
            }
        )
        .fromTo(
            '.side-timeline',
            {
                autoAlpha: 0,
                x: -75,
            },
            {
                autoAlpha: 1,
                x: 0,
                ease: 'back.out(1.4)',
                duration: 5,
            },
            '<'
        )

    journey.pinWhileAnimating({
        end: '+=400%',
        onEnter: () => {
            journey.enterStage(2)
        },
        onLeaveBack: () => {
            journey.leaveBackStage(2)
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({
        animation: tl_2_1,
        start: 'top top+=1px',
        end: '+=375%',
    })

    /*** STEP 2.2 ***/
    journey.step = '2-2'
    const tl_2_2 = gsap.timeline()

    tl_2_2
        .from(journey.step.querySelector('.pepa'), {
            scale: 0.9,
            duration: 1,
            ease: 'elastic.out(1, 1)',
        })
        .fromTo(
            journey.step.querySelector('.animated-text'),
            {
                autoAlpha: 0,
                yPercent: -50,
                y: 50,
                scale: 0.9,
            },
            {
                autoAlpha: 1,
                y: 0,
                scale: 1,
                duration: 2,
                ease: 'elastic.out(1, 1)',
            },
            '-=0.5'
        )

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            journey.pinWhileAnimating({
                pinSpacing: false,
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating()
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({
        animation: tl_2_2,
        scrub: false,
        toggleActions: 'play none none reverse',
    })

    /*** STEP 2.3 ***/
    journey.step = '2-3'
    const step_2_3_4 = journey.getStep('2-3_4')
    const tl_2_3 = gsap.timeline()
    journey.addSideStepTimeline(tl_2_3)

    tl_2_3
        .fromTo(
            journey.step.querySelector('.bus'),
            {
                scale: 1.1,
                xPercent: 25,
                y: 110,
            },
            {
                scale: 0.8,
                xPercent: -10,
                y: 10,
                duration: 15,
            }
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onLeaveBack
                journey.leaveSideStep(2)
            }
        })
        .set(step_2_3_4, {
            autoAlpha: 1,
        })
        .fromTo(
            step_2_3_4,
            {
                x: '100vw',
            },
            {
                x: 0,
                duration: 15,
            },
            '+=3'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onEnter
                journey.enterSideStep(2, true)
            }
        })
        .to(
            step_2_3_4.querySelector('.step-text'),
            {
                y: (index, target, targets) => {
                    return -(vh + target.offsetHeight + 48) // For some reason, the offsetHeight is 48px smaller when the calculations are made
                },
                duration: 35,
                ease: 'slow(0.1, 0.4, false)',
            },
            '+=2'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onEnterBack
                journey.enterSideStep(2)
            }
        })
        .to(
            step_2_3_4,
            {
                x: '100vw',
                duration: 20,
            },
            '+=1'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onLeave
                journey.leaveSideStep(2)
            }
        }, '-=1')

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            // Previous step
            journey.pinWhileAnimating({ start: 'top center' })
            // Current step
            journey.pinWhileAnimating({
                end: '+=800%',
                pinSpacing: false,
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({ end: '+=800%' })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({ animation: tl_2_3, end: '+=775%' })

    /*** STEP 2.4 ***/
    journey.step = '2-4'
    const step_2_4_5 = journey.getStep('2-4_5')
    const tl_2_4 = gsap.timeline()
    journey.addSideStepTimeline(tl_2_4)

    tl_2_4
        .fromTo(
            journey.step.querySelector('.bus'),
            {
                x: '100vw',
            },
            {
                x: 0,
                ease: 'power2.out',
                duration: 15,
            }
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onLeaveBack
                journey.leaveSideStep(2)
            }
        })
        .set(step_2_4_5, {
            autoAlpha: 1,
        })
        .fromTo(
            step_2_4_5,
            {
                x: '100vw',
            },
            {
                x: 0,
                duration: 15,
            },
            '+=3'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onEnter
                journey.enterSideStep(2)
            }
        })
        .to(
            step_2_4_5.querySelector('.step-text'),
            {
                y: (index, target, targets) => {
                    return -(vh + target.offsetHeight + 48) // For some reason, the offsetHeight is 48px smaller when the calculations are made
                },
                duration: 35,
                ease: 'slow(0.1, 0.4, false)',
            },
            '+=2'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onEnterBack
                journey.enterSideStep(2)
            }
        })
        .to(
            step_2_4_5,
            {
                x: '100vw',
                duration: 20,
            },
            '+=1'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onLeave
                journey.leaveSideStep(2)
            }
        }, '-=1')

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            // Previous step
            journey.pinWhileAnimating({
                start: 'top center',
                end: '+=800%',
            })
            // Current step
            journey.pinWhileAnimating({
                end: '+=975%',
                pinSpacing: false,
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({ end: '+=975%' })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({ animation: tl_2_4, end: '+=950%' })

    /*** STEP 2.5 ***/
    journey.step = '2-5'
    const tl_2_5 = gsap.timeline()
    const tl_heart = gsap.timeline()

    tl_heart
        .fromTo(
            journey.step.querySelector('.heart'),
            {
                autoAlpha: 0,
                scale: 0.25,
            },
            {
                autoAlpha: 1,
                scale: 0.9,
                duration: 2,
                ease: 'elastic.out(1, 0.4)',
            }
        )
        .to(journey.step.querySelector('.heart'), {
            scale: 1.05,
            duration: 0.75,
            repeat: -1,
            yoyo: true,
        })

    tl_2_5.to(journey.step.querySelector('.main'), {
        scale: 1.5,
    })

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            // Previous step
            journey.pinWhileAnimating({
                start: 'top center',
                end: '+=975%',
            })
            // Current step
            journey.pinWhileAnimating({
                end: '+=275%',
                pinSpacing: false,
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({ end: '+=275%' })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({
        animation: tl_heart,
        end: null,
        scrub: false,
    })
    journey.triggerAnimation({
        animation: tl_2_5,
        end: '+=250%',
    })

    /*** STEP 2.6 ***/
    journey.step = '2-6'
    const tl_2_6 = gsap.timeline()

    tl_2_6
        .fromTo(
            journey.step.querySelector('.tranvia'),
            {
                autoAlpha: 0,
            },
            {
                autoAlpha: 1,
            }
        )
        .fromTo(
            journey.step.querySelector('.autobus'),
            {
                autoAlpha: 0,
            },
            {
                autoAlpha: 1,
            }
        )

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            // Previous step
            journey.pinWhileAnimating({
                start: 'top center',
                end: '+=275%',
            })
            // Current step
            journey.pinWhileAnimating({
                end: '+=150%',
                onLeave: (self) => {
                    journey.leaveStage(2, self)
                },
                onEnterBack: (self) => {
                    journey.enterBackStage(2, self)
                },
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({
                end: '+=150%',
                onLeave: (self) => {
                    journey.leaveStage(2, self)
                },
                onEnterBack: (self) => {
                    journey.enterBackStage(2, self)
                },
            })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({ animation: tl_2_6, end: '+=125%' })
    stageSelectorST = journey.showStageSelector()
    journey.setInitialized(2)
    return true
}

const initializeStage3 = (journey) => {
    /****************
     *** STAGE 3 ***
     *****************/
    journey.stage = 3
    const stage_3 = journey.stage

    /*** STEP 3.1 ***/
    journey.step = '3-1'
    const step_3_1_2 = journey.getStep('3-1_2')
    const tl_3_1 = gsap.timeline()
    journey.addSideStepTimeline(tl_3_1)

    tl_3_1
        .to(journey.step.querySelector('.stage-introduction'), {
            y: (index, target, targets) => -(vh + target.offsetHeight + 64),
            duration: 35,
            ease: 'slow(0.1, 0.4, false)',
        })
        .fromTo(
            journey.step.querySelector('.fullheight-img'),
            {
                autoAlpha: 1,
            },
            {
                autoAlpha: 0,
                duration: 25,
            }
        )
        .fromTo(
            journey.step.querySelector('.layer.cover'),
            {
                autoAlpha: 0,
            },
            {
                autoAlpha: 1,
                duration: 25,
            },
            '<'
        )
        .fromTo(
            journey.step.querySelector('.zepelin'),
            {
                xPercent: -100,
            },
            {
                xPercent: 0,
                x: () => vw,
                duration: 45,
            },
            '-=10'
        )
        .fromTo(
            journey.step.querySelector('.zepelin'),
            {
                y: -15,
            },
            {
                y: 15,
                duration: 20,
                yoyo: true,
                repeat: 2,
                ease: 'bounce.inOut',
            },
            '<'
        )
        .fromTo(
            stage_3.querySelector('.stage-header'),
            {
                autoAlpha: 0,
                y: -50,
            },
            {
                autoAlpha: 1,
                y: 0,
                ease: 'back.out(1.4)',
                duration: 5,
            },
            '-=5'
        )
        .fromTo(
            '.side-timeline',
            {
                autoAlpha: 0,
                x: -75,
            },
            {
                autoAlpha: 1,
                x: 0,
                ease: 'back.out(1.4)',
                duration: 5,
            },
            '<'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onLeaveBack
                journey.leaveSideStep(3)
            }
        })
        .set(step_3_1_2, {
            autoAlpha: 1,
        })
        .fromTo(
            step_3_1_2,
            {
                x: '100vw',
            },
            {
                x: 0,
                duration: 20,
            },
            '+=3'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onEnter
                journey.enterSideStep(3, true)
            }
        })
        .to(
            step_3_1_2.querySelector('.step-text'),
            {
                y: (index, target, targets) => {
                    return -(vh + target.offsetHeight + 24) // For some reason, the offsetHeight is 24px smaller when the calculations are made
                },
                duration: 50,
                ease: 'slow(0.1, 0.4, false)',
            },
            '+=2'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onEnterBack
                journey.enterSideStep(3)
            }
        })
        .to(
            step_3_1_2,
            {
                x: '100vw',
                duration: 20,
            },
            '+=1'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onLeave
                journey.leaveSideStep(3)
            }
        }, '-=1')

    journey.pinWhileAnimating({
        end: '+=1500%',
        onEnter: () => {
            journey.enterStage(3)
        },
        onLeaveBack: () => {
            journey.leaveBackStage(3)
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({
        animation: tl_3_1,
        start: 'top top+=1px',
        end: '+=1475%',
    })

    /*** STEP 3.2 ***/
    journey.step = '3-2'
    const tl_3_2 = gsap.timeline()

    tl_3_2
        .from(journey.step.querySelector('.subject'), {
            scale: 0.9,
            duration: 1,
            ease: 'elastic.out(1, 1)',
        })
        .fromTo(
            journey.step.querySelector('.animated-text'),
            {
                autoAlpha: 0,
                yPercent: -50,
                y: 50,
                scale: 0.9,
            },
            {
                autoAlpha: 1,
                y: 0,
                scale: 1,
                duration: 2,
                ease: 'elastic.out(1, 1)',
            },
            '-=0.5'
        )

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            journey.pinWhileAnimating({ pinSpacing: false })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating()
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({
        animation: tl_3_2,
        scrub: false,
        toggleActions: 'play none none reverse',
    })

    /*** STEP 3.3 ***/
    journey.step = '3-3'
    const step_3_3_4 = journey.getStep('3-3_4')
    const tl_3_3 = gsap.timeline()
    journey.addSideStepTimeline(tl_3_3)

    tl_3_3
        .to(journey.step.querySelector('.main'), {
            width: '150%',
            duration: 15,
        })
        .to(
            journey.step.querySelector('.main'),
            {
                xPercent: -30,
                duration: 20,
            },
            '-=5'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onLeaveBack
                journey.leaveSideStep(3)
            }
        })
        .set(step_3_3_4, {
            autoAlpha: 1,
        })
        .fromTo(
            step_3_3_4,
            {
                x: '100vw',
            },
            {
                x: 0,
                duration: 20,
            },
            '+=3'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onEnter
                journey.enterSideStep(3)
            }
        })
        .to(
            step_3_3_4.querySelector('.step-text'),
            {
                y: (index, target, targets) => {
                    return -(vh + target.offsetHeight + 24) // For some reason, the offsetHeight is 24px smaller when the calculations are made
                },
                duration: 50,
                ease: 'slow(0.1, 0.4, false)',
            },
            '+=2'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onEnterBack
                journey.enterSideStep(3)
            }
        })
        .to(
            step_3_3_4,
            {
                x: '100vw',
                duration: 20,
            },
            '+=1'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onLeave
                journey.leaveSideStep(3)
            }
        }, '-=1')

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            // Previous step
            journey.pinWhileAnimating({ start: 'top center' })
            // Current step
            journey.pinWhileAnimating({
                end: '+=850%',
                pinSpacing: false,
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({ end: '+=850%' })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({ animation: tl_3_3, end: '+=825%' })

    /*** STEP 3.4 ***/
    journey.step = '3-4'
    const tl_3_4 = gsap.timeline()

    tl_3_4
        .fromTo(
            journey.step.querySelector('.tranvia'),
            {
                autoAlpha: 0,
            },
            {
                autoAlpha: 1,
            }
        )
        .fromTo(
            journey.step.querySelector('.autobus'),
            {
                autoAlpha: 0,
            },
            {
                autoAlpha: 1,
            }
        )

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            // Previous step
            journey.pinWhileAnimating({
                start: 'top center',
                end: '+=850%',
            })
            // Current step
            journey.pinWhileAnimating({
                end: '+=150%',
                onLeave: (self) => {
                    journey.leaveStage(3, self)
                },
                onEnterBack: (self) => {
                    journey.enterBackStage(3, self)
                },
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({
                end: '+=150%',
                onLeave: (self) => {
                    journey.leaveStage(3, self)
                },
                onEnterBack: (self) => {
                    journey.enterBackStage(3, self)
                },
            })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({ animation: tl_3_4, end: '+=125%' })
    stageSelectorST = journey.showStageSelector()
    journey.setInitialized(3)
    return true
}

const initializeStage4 = (journey) => {
    /****************
     *** STAGE 4 ***
     *****************/
    journey.stage = 4
    const stage_4 = journey.stage

    /*** STEP 4.1 ***/
    journey.step = '4-1'
    const tl_4_1 = gsap.timeline()

    tl_4_1
        .to(journey.step.querySelector('.stage-introduction'), {
            y: (index, target, targets) => -(vh + target.offsetHeight + 64),
            duration: 50,
            ease: 'slow(0.1, 0.4, false)',
        })
        .fromTo(
            journey.step.querySelector('.cover-first'),
            {
                autoAlpha: 1,
            },
            {
                autoAlpha: 0,
                duration: 40,
            }
        )
        .fromTo(
            journey.step.querySelector('.layer.cover'),
            {
                autoAlpha: 0,
            },
            {
                autoAlpha: 1,
                duration: 40,
            },
            '<'
        )
        .fromTo(
            journey.step.querySelector('.layer.cover'),
            {
                scale: 1.05,
            },
            {
                scale: 1.15,
                duration: 20,
            },
            '<'
        )
        .fromTo(
            journey.step.querySelector('.marco'),
            {
                autoAlpha: 0,
                scale: 1.25,
            },
            {
                autoAlpha: 1,
                scale: 1,
                duration: 20,
            },
            '-=20'
        )
        .fromTo(
            stage_4.querySelector('.stage-header'),
            {
                autoAlpha: 0,
                y: -50,
            },
            {
                autoAlpha: 1,
                y: 0,
                ease: 'back.out(1.4)',
                duration: 5,
            }
        )
        .fromTo(
            '.side-timeline',
            {
                autoAlpha: 0,
                x: -75,
            },
            {
                autoAlpha: 1,
                x: 0,
                ease: 'back.out(1.4)',
                duration: 5,
            },
            '<'
        )

    journey.pinWhileAnimating({
        end: '+=400%',
        onEnter: () => {
            journey.enterStage(4)
        },
        onLeaveBack: () => {
            journey.leaveBackStage(4)
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({
        animation: tl_4_1,
        start: 'top top+=1px',
        end: '+=375%',
    })

    /*** STEP 4.2 ***/
    journey.step = '4-2'
    const tl_4_2 = gsap.timeline()

    tl_4_2
        .from(journey.step.querySelector('.pepa'), {
            scale: 0.9,
            duration: 1,
            ease: 'elastic.out(1, 1)',
        })
        .fromTo(
            journey.step.querySelector('.animated-text'),
            {
                autoAlpha: 0,
                yPercent: -50,
                y: 50,
                scale: 0.9,
            },
            {
                autoAlpha: 1,
                y: 0,
                scale: 1,
                duration: 2,
                ease: 'elastic.out(1, 1)',
            },
            '-=0.5'
        )

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            journey.pinWhileAnimating({ pinSpacing: false })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating()
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({
        animation: tl_4_2,
        scrub: false,
        toggleActions: 'play none none reverse',
    })

    /*** STEP 4.3 ***/
    journey.step = '4-3'
    const step_4_3_4 = journey.getStep('4-3_4')
    const tl_4_3 = gsap.timeline()
    journey.addSideStepTimeline(tl_4_3)

    tl_4_3
        .fromTo(
            journey.step.querySelector('.bus-fg'),
            {
                xPercent: -15,
                scale: 0.8,
            },
            {
                xPercent: 0,
                scale: 1,
                ease: 'power2.out',
                duration: 25,
            }
        )
        .fromTo(
            journey.step.querySelector('.bus-bg'),
            {
                xPercent: 20,
                yPercent: 2,
                scale: 1.1,
            },
            {
                xPercent: 0,
                yPercent: 0,
                scale: 1,
                ease: 'power2.out',
                delay: 0.01,
                duration: 25,
            },
            '<'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onLeaveBack
                journey.leaveSideStep(4)
            }
        })
        .set(step_4_3_4, {
            autoAlpha: 1,
        })
        .fromTo(
            step_4_3_4,
            {
                x: '100vw',
            },
            {
                x: 0,
                duration: 20,
            },
            '+=3'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onEnter
                journey.enterSideStep(4, true)
            }
        })
        .to(
            step_4_3_4.querySelector('.step-text'),
            {
                y: (index, target, targets) => {
                    return -(vh + target.offsetHeight + 48) // For some reason, the offsetHeight is 48px smaller when the calculations are made
                },
                duration: 50,
                ease: 'slow(0.1, 0.4, false)',
            },
            '+=2'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onEnterBack
                journey.enterSideStep(4)
            }
        })
        .to(
            step_4_3_4,
            {
                x: '100vw',
                duration: 20,
            },
            '+=1'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onLeave
                journey.leaveSideStep(4)
            }
        }, '-=1')

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            // Previous step
            journey.pinWhileAnimating({ start: 'top center' })
            // Current step
            journey.pinWhileAnimating({
                end: '+=950%',
                pinSpacing: false,
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({ end: '+=950%' })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({ animation: tl_4_3, end: '+=925%' })

    /*** STEP 4.4 ***/
    journey.step = '4-4'
    const step_4_4_5 = journey.getStep('4-4_5')
    const tl_4_4 = gsap.timeline()
    journey.addSideStepTimeline(tl_4_4)

    tl_4_4
        .fromTo(
            journey.step.querySelector('.avion-bg'),
            {
                xPercent: 40,
            },
            {
                xPercent: -40,
                duration: 18,
            }
        )
        .fromTo(
            journey.step.querySelector('.avion-bg'),
            {
                y: 3,
            },
            {
                y: -3,
                repeat: 5,
                yoyo: true,
                duration: 3,
                ease: 'power1.inOut',
            },
            '<'
        )
        .fromTo(
            journey.step.querySelector('.avion-fg'),
            {
                xPercent: 50,
            },
            {
                xPercent: -90,
                duration: 12,
            },
            '<'
        )
        .fromTo(
            journey.step.querySelector('.avion-fg'),
            {
                y: 3,
            },
            {
                y: -3,
                repeat: 5,
                yoyo: true,
                duration: 2,
                ease: 'back.inOut(1)',
            },
            '<'
        )
        .fromTo(
            journey.step.querySelector('.bombas'),
            {
                xPercent: 50,
            },
            {
                xPercent: -40,
                duration: 9,
            },
            '<'
        )
        .fromTo(
            journey.step.querySelector('.bombas'),
            {
                y: -85,
            },
            {
                y: '50%',
                duration: 9,
            },
            '<0.75'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onLeaveBack
                journey.leaveSideStep(4)
            }
        })
        .set(step_4_4_5, {
            autoAlpha: 1,
        })
        .fromTo(
            step_4_4_5,
            {
                x: '100vw',
            },
            {
                x: 0,
                duration: 20,
            },
            '+=3'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onEnter
                journey.enterSideStep(4)
            }
        })
        .to(
            step_4_4_5.querySelector('.step-text'),
            {
                y: (index, target, targets) => {
                    return -(vh + target.offsetHeight + 48) // For some reason, the offsetHeight is 48px smaller when the calculations are made
                },
                duration: 35,
                ease: 'slow(0.1, 0.4, false)',
            },
            '+=2'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onEnterBack
                journey.enterSideStep(4)
            }
        })
        .to(
            step_4_4_5,
            {
                x: '100vw',
                duration: 20,
            },
            '+=1'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onLeave
                journey.leaveSideStep(4)
            }
        }, '-=1')

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            // Previous step
            journey.pinWhileAnimating({
                start: 'top center',
                end: '+=950%',
            })
            // Current step
            journey.pinWhileAnimating({
                end: '+=1125%',
                pinSpacing: false,
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({ end: '+=1125%' })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({ animation: tl_4_4, end: '+=1100%' })

    /*** STEP 4.5 ***/
    journey.step = '4-5'
    const tl_4_5 = gsap.timeline()

    tl_4_5.to(journey.step.querySelector('.main'), {
        scale: 1.5,
        xPercent: -8,
        yPercent: -8,
    })

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            // Previous step
            journey.pinWhileAnimating({
                start: 'top center',
                end: '+=1125%',
            })
            // Current step
            journey.pinWhileAnimating({
                end: '+=200%',
                pinSpacing: false,
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({ end: '+=200%' })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({ animation: tl_4_5, end: '+=175%' })

    /*** STEP 4.6 ***/
    journey.step = '4-6'
    const tl_4_6 = gsap.timeline()

    tl_4_6.fromTo(
        journey.step.querySelector('.main'),
        {
            xPercent: -25,
        },
        {
            xPercent: 0,
        }
    )

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            // Previous step
            journey.pinWhileAnimating({
                start: 'top center',
                end: '+=200%',
            })
            // Current step
            journey.pinWhileAnimating({
                end: '+=200%',
                onLeave: (self) => {
                    journey.leaveStage(4, self)
                },
                onEnterBack: (self) => {
                    journey.enterBackStage(4, self)
                },
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({
                end: '+=200%',
                onLeave: (self) => {
                    journey.leaveStage(4, self)
                },
                onEnterBack: (self) => {
                    journey.enterBackStage(4, self)
                },
            })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({ animation: tl_4_6, end: '+=175%' })
    stageSelectorST = journey.showStageSelector()
    journey.setInitialized(4)
    return true
}

const initializeStage5 = (journey) => {
    /****************
     *** STAGE 5 ***
     *****************/
    journey.stage = 5
    const stage_5 = journey.stage

    /*** STEP 5.1 ***/
    journey.step = '5-1'
    const step_5_1_2 = journey.getStep('5-1_2')
    const tl_5_1 = gsap.timeline()
    journey.addSideStepTimeline(tl_5_1)

    tl_5_1
        .to(journey.step.querySelector('.stage-introduction'), {
            y: (index, target, targets) => -(vh + target.offsetHeight + 64),
            duration: 50,
            ease: 'slow(0.1, 0.4, false)',
        })
        .fromTo(
            journey.step.querySelector('.fullheight-img'),
            {
                autoAlpha: 1,
            },
            {
                autoAlpha: 0,
                duration: 40,
            }
        )
        .fromTo(
            journey.step.querySelector('.layer.cover'),
            {
                autoAlpha: 0,
            },
            {
                autoAlpha: 1,
                duration: 40,
            },
            '<'
        )
        .fromTo(
            journey.step.querySelector('.layer.cover'),
            {
                scale: 1.1,
            },
            {
                scale: 1.2,
                duration: 20,
            },
            '<'
        )
        .fromTo(
            journey.step.querySelector('.marco'),
            {
                autoAlpha: 0,
                scale: 1.25,
            },
            {
                autoAlpha: 1,
                scale: 1,
                duration: 20,
            },
            '-=20'
        )
        .fromTo(
            stage_5.querySelector('.stage-header'),
            {
                autoAlpha: 0,
                y: -50,
            },
            {
                autoAlpha: 1,
                y: 0,
                ease: 'back.out(1.4)',
                duration: 5,
            }
        )
        .fromTo(
            '.side-timeline',
            {
                autoAlpha: 0,
                x: -75,
            },
            {
                autoAlpha: 1,
                x: 0,
                ease: 'back.out(1.4)',
                duration: 5,
            },
            '<'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onLeaveBack
                journey.leaveSideStep(5)
            }
        })
        .set(step_5_1_2, {
            autoAlpha: 1,
        })
        .fromTo(
            step_5_1_2,
            {
                x: '100vw',
            },
            {
                x: 0,
                duration: 20,
            },
            '+=3'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onEnter
                journey.enterSideStep(5, true)
            }
        })
        .to(
            step_5_1_2.querySelector('.step-text'),
            {
                y: (index, target, targets) => {
                    return -(vh + target.offsetHeight + 24) // For some reason, the offsetHeight is 24px smaller when the calculations are made
                },
                duration: 55,
                ease: 'slow(0.1, 0.4, false)',
            },
            '+=2'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onEnterBack
                journey.enterSideStep(5)
            }
        })
        .to(
            step_5_1_2,
            {
                x: '100vw',
                duration: 20,
            },
            '+=1'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onLeave
                journey.leaveSideStep(5)
            }
        }, '-=1')

    journey.pinWhileAnimating({
        end: '+=1350%',
        onEnter: () => {
            journey.enterStage(5)
        },
        onLeaveBack: () => {
            journey.leaveBackStage(5)
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({
        animation: tl_5_1,
        start: 'top top+=1px',
        end: '+=1325%',
    })

    /*** STEP 5.2 ***/
    journey.step = '5-2'
    const step_5_2_3 = journey.getStep('5-2_3')
    const tl_5_2 = gsap.timeline()
    journey.addSideStepTimeline(tl_5_2)

    tl_5_2
        .fromTo(
            journey.step.querySelector('.bus'),
            {
                x: 300,
                scale: 0.9,
            },
            {
                x: 0,
                scale: 1,
                ease: 'power2.out',
                duration: 20,
            }
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onLeaveBack
                journey.leaveSideStep(5)
            }
        })
        .set(step_5_2_3, {
            autoAlpha: 1,
        })
        .fromTo(
            step_5_2_3,
            {
                x: '100vw',
            },
            {
                x: 0,
                duration: 20,
            },
            '+=3'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onEnter
                journey.enterSideStep(5)
            }
        })
        .to(
            step_5_2_3.querySelector('.step-text'),
            {
                y: (index, target, targets) => {
                    return -(vh + target.offsetHeight + 24) // For some reason, the offsetHeight is 24px smaller when the calculations are made
                },
                duration: 55,
                ease: 'slow(0.1, 0.4, false)',
            },
            '+=2'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onEnterBack
                journey.enterSideStep(5)
            }
        })
        .to(
            step_5_2_3,
            {
                x: '100vw',
                duration: 20,
            },
            '+=1'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onLeave
                journey.leaveSideStep(5)
            }
        }, '-=1')

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            journey.pinWhileAnimating({
                end: '+=925%',
                pinSpacing: false,
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({ end: '+=925%' })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({ animation: tl_5_2, end: '+=900%' })

    /*** STEP 5.3 ***/
    journey.step = '5-3'
    const tl_5_3 = gsap.timeline()

    tl_5_3.to(journey.step.querySelector('.main'), {
        scale: 1.5,
        x: -50,
    })

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            // Previous step
            journey.pinWhileAnimating({
                start: 'top center',
                end: '+=925%',
            })
            // Current step
            journey.pinWhileAnimating({
                end: '+=225%',
                pinSpacing: false,
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({ end: '+=225%' })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({ animation: tl_5_3, end: '+=200%' })

    /*** STEP 5.4 ***/
    journey.step = '5-4'
    const tl_5_4 = gsap.timeline()

    tl_5_4
        .from(journey.step.querySelector('.oriol'), {
            scale: 0.9,
            duration: 1,
            ease: 'elastic.out(1, 1)',
        })
        .fromTo(
            journey.step.querySelector('.animated-text'),
            {
                autoAlpha: 0,
                yPercent: -50,
                y: 50,
                scale: 0.9,
            },
            {
                autoAlpha: 1,
                y: 0,
                scale: 1,
                duration: 2,
                ease: 'elastic.out(1, 1)',
            },
            '-=0.5'
        )

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            // Previous step
            journey.pinWhileAnimating({
                start: 'top center',
                end: '+=225%',
            })
            // Current step
            journey.pinWhileAnimating({
                onLeave: (self) => {
                    journey.leaveStage(5, self)
                },
                onEnterBack: (self) => {
                    journey.enterBackStage(5, self)
                },
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({
                onLeave: (self) => {
                    journey.leaveStage(5, self)
                },
                onEnterBack: (self) => {
                    journey.enterBackStage(5, self)
                },
            })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({
        animation: tl_5_4,
        scrub: false,
        toggleActions: 'play none none reverse',
    })
    stageSelectorST = journey.showStageSelector()
    journey.setInitialized(5)
    return true
}

const initializeStage6 = (journey) => {
    /****************
     *** STAGE 6 ***
     *****************/
    journey.stage = 6
    const stage_6 = journey.stage

    /*** STEP 6.1 ***/
    journey.step = '6-1'
    const tl_6_1 = gsap.timeline()

    tl_6_1
        .to(journey.step.querySelector('.stage-introduction'), {
            y: (index, target, targets) => -(vh + target.offsetHeight + 64),
            duration: 50,
            ease: 'slow(0.1, 0.4, false)',
        })
        .fromTo(
            journey.step.querySelector('.fullheight-img'),
            {
                autoAlpha: 1,
            },
            {
                autoAlpha: 0,
                duration: 40,
            }
        )
        .fromTo(
            journey.step.querySelector('.layer.cover'),
            {
                autoAlpha: 0,
            },
            {
                autoAlpha: 1,
                duration: 40,
            },
            '<'
        )
        .fromTo(
            journey.step.querySelector('.layer.cover'),
            {
                scale: 1.15,
            },
            {
                scale: 1.25,
                duration: 20,
            },
            '<'
        )
        .fromTo(
            journey.step.querySelector('.marco'),
            {
                autoAlpha: 0,
                scale: 1.25,
            },
            {
                autoAlpha: 1,
                scale: 1,
                duration: 20,
            },
            '-=20'
        )
        .fromTo(
            stage_6.querySelector('.stage-header'),
            {
                autoAlpha: 0,
                y: -50,
            },
            {
                autoAlpha: 1,
                y: 0,
                ease: 'back.out(1.4)',
                duration: 5,
            }
        )
        .fromTo(
            '.side-timeline',
            {
                autoAlpha: 0,
                x: -75,
            },
            {
                autoAlpha: 1,
                x: 0,
                ease: 'back.out(1.4)',
                duration: 5,
            },
            '<'
        )

    journey.pinWhileAnimating({
        end: '+=400%',
        onEnter: () => {
            journey.enterStage(6)
        },
        onLeaveBack: () => {
            journey.leaveBackStage(6)
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({
        animation: tl_6_1,
        start: 'top top+=1px',
        end: '+=375%',
    })

    /*** STEP 6.2 ***/
    journey.step = '6-2'
    const step_6_2_3 = journey.getStep('6-2_3')
    const tl_6_2 = gsap.timeline()
    const tl_6_2_3 = gsap.timeline()
    journey.addSideStepTimeline(tl_6_2_3)

    tl_6_2
        .from(journey.step.querySelector('.pepa'), {
            scale: 0.9,
            duration: 1,
            ease: 'elastic.out(1, 1)',
        })
        .fromTo(
            journey.step.querySelector('.animated-text'),
            {
                autoAlpha: 0,
                yPercent: -50,
                y: 50,
                scale: 0.9,
            },
            {
                autoAlpha: 1,
                y: 0,
                scale: 1,
                duration: 2,
                ease: 'elastic.out(1, 1)',
            },
            '-=0.5'
        )

    tl_6_2_3
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onLeaveBack
                journey.leaveSideStep(6)
            }
        }, '10')
        .set(step_6_2_3, {
            autoAlpha: 1,
        })
        .fromTo(
            step_6_2_3,
            {
                x: '100vw',
            },
            {
                x: 0,
                duration: 20,
            },
            '+=3'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onEnter
                journey.enterSideStep(6, true)
            }
        })
        .to(
            step_6_2_3.querySelector('.step-text'),
            {
                y: (index, target, targets) => {
                    return -(vh + target.offsetHeight + 24) // For some reason, the offsetHeight is 24px smaller when the calculations are made
                },
                duration: 55,
                ease: 'slow(0.1, 0.4, false)',
            },
            '+=2'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onEnterBack
                journey.enterSideStep(6)
            }
        })
        .to(
            step_6_2_3,
            {
                x: '100vw',
                duration: 20,
            },
            '+=1'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onLeave
                journey.leaveSideStep(6)
            }
        }, '-=1')

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            journey.pinWhileAnimating({
                end: '+=850%',
                pinSpacing: false,
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({ end: '+=850%' })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({
        animation: tl_6_2,
        scrub: false,
        toggleActions: 'play none none reverse',
    })
    journey.triggerAnimation({
        animation: tl_6_2_3,
        end: '+=750%',
    })

    /*** STEP 6.3 ***/
    journey.step = '6-3'
    const tl_6_3 = gsap.timeline()

    tl_6_3.fromTo(
        journey.step.querySelector('.main'),
        {
            scale: 1.5,
            x: -100,
            y: -50,
        },
        {
            scale: 1,
            x: 0,
            y: 0,
        }
    )

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            // Previous step
            journey.pinWhileAnimating({
                start: 'top center',
                end: '+=850%',
            })
            // Current step
            journey.pinWhileAnimating({
                end: '+=225%',
                pinSpacing: false,
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({ end: '+=225%' })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({ animation: tl_6_3, end: '+=200%' })

    /*** STEP 6.4 ***/
    journey.step = '6-4'
    const step_6_4_5 = journey.getStep('6-4_5')
    const tl_6_4 = gsap.timeline()
    journey.addSideStepTimeline(tl_6_4)

    tl_6_4
        .to(journey.step.querySelector('.main'), {
            scale: 1.75,
            x: -200,
            duration: 20,
        })
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onLeaveBack
                journey.leaveSideStep(6)
            }
        })
        .set(step_6_4_5, {
            autoAlpha: 1,
        })
        .fromTo(
            step_6_4_5,
            {
                x: '100vw',
            },
            {
                x: 0,
                duration: 20,
            },
            '+=3'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onEnter
                journey.enterSideStep(6)
            }
        })
        .to(
            step_6_4_5.querySelector('.step-text'),
            {
                y: (index, target, targets) => {
                    return -(vh + target.offsetHeight + 24) // For some reason, the offsetHeight is 24px smaller when the calculations are made
                },
                duration: 55,
                ease: 'slow(0.1, 0.4, false)',
            },
            '+=2'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onEnterBack
                journey.enterSideStep(6)
            }
        })
        .to(
            step_6_4_5,
            {
                x: '100vw',
                duration: 20,
            },
            '+=1'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onLeave
                journey.leaveSideStep(6)
            }
        }, '-=1')

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            // Previous step
            journey.pinWhileAnimating({
                start: 'top center',
                end: '+=225%',
            })
            // Current step
            journey.pinWhileAnimating({
                end: '+=950%',
                pinSpacing: false,
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({ end: '+=950%' })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({ animation: tl_6_4, end: '+=925%' })

    /*** STEP 6.5 ***/
    journey.step = '6-5'
    const tl_6_5 = gsap.timeline()

    tl_6_5
        .fromTo(
            journey.step.querySelector('.bus'),
            {
                scale: 0.9,
                x: 65,
            },
            {
                scale: 1,
                x: 0,
                ease: 'power2.out',
            }
        )
        .to(
            journey.step.querySelector('.layered-image'),
            {
                scale: 1.5,
                x: -50,
            },
            '<0.01'
        )
        .fromTo(
            journey.step.querySelector('.car'),
            {
                xPercent: 50,
                y: -10,
            },
            {
                xPercent: 0,
                y: 0,
            },
            '<0.05'
        )

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            // Previous step
            journey.pinWhileAnimating({
                start: 'top center',
                end: '+=950%',
            })
            // Current step
            journey.pinWhileAnimating({
                end: '+=225%',
                onLeave: (self) => {
                    journey.leaveStage(6, self)
                },
                onEnterBack: (self) => {
                    journey.enterBackStage(6, self)
                },
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({
                end: '+=225%',
                onLeave: (self) => {
                    journey.leaveStage(6, self)
                },
                onEnterBack: (self) => {
                    journey.enterBackStage(6, self)
                },
            })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({
        animation: tl_6_5,
        end: '+=200%',
    })
    stageSelectorST = journey.showStageSelector()
    journey.setInitialized(6)
    return true
}

const initializeStage7 = (journey) => {
    /****************
     *** STAGE 7 ***
     *****************/
    journey.stage = 7
    const stage_7 = journey.stage

    /*** STEP 7.1 ***/
    journey.step = '7-1'
    const tl_7_1 = gsap.timeline()

    tl_7_1
        .to(journey.step.querySelector('.stage-introduction'), {
            y: (index, target, targets) => -(vh + target.offsetHeight + 64),
            duration: 50,
            ease: 'slow(0.1, 0.4, false)',
        })
        .fromTo(
            journey.step.querySelector('.fullheight-img'),
            {
                autoAlpha: 1,
            },
            {
                autoAlpha: 0,
                duration: 40,
            }
        )
        .fromTo(
            journey.step.querySelector('.layer.cover'),
            {
                autoAlpha: 0,
            },
            {
                autoAlpha: 1,
                duration: 40,
            },
            '<'
        )
        .fromTo(
            journey.step.querySelector('.layer.cover'),
            {
                scale: 1.25,
            },
            {
                scale: 1.35,
                duration: 20,
            },
            '<'
        )
        .fromTo(
            stage_7.querySelector('.stage-header'),
            {
                autoAlpha: 0,
                y: -50,
            },
            {
                autoAlpha: 1,
                y: 0,
                ease: 'back.out(1.4)',
                duration: 5,
            }
        )
        .fromTo(
            '.side-timeline',
            {
                autoAlpha: 0,
                x: -75,
            },
            {
                autoAlpha: 1,
                x: 0,
                ease: 'back.out(1.4)',
                duration: 5,
            },
            '<'
        )

    journey.pinWhileAnimating({
        end: '+=400%',
        onEnter: () => {
            journey.enterStage(7)
        },
        onLeaveBack: () => {
            journey.leaveBackStage(7)
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({
        animation: tl_7_1,
        start: 'top top+=1px',
        end: '+=375%',
    })

    /*** STEP 7.2 ***/
    journey.step = '7-2'
    const step_7_2_3 = journey.getStep('7-2_3')
    const tl_7_2 = gsap.timeline()
    journey.addSideStepTimeline(tl_7_2)

    tl_7_2
        .to(journey.step.querySelector('.main'), {
            xPercent: -35,
            duration: 20,
        })
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onLeaveBack
                journey.leaveSideStep(7)
            }
        })
        .set(step_7_2_3, {
            autoAlpha: 1,
        })
        .fromTo(
            step_7_2_3,
            {
                x: '100vw',
            },
            {
                x: 0,
                duration: 20,
            },
            '+=3'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onEnter
                journey.enterSideStep(7, true)
            }
        })
        .to(
            step_7_2_3.querySelector('.step-text'),
            {
                y: (index, target, targets) => {
                    return -(vh + target.offsetHeight + 24) // For some reason, the offsetHeight is 24px smaller when the calculations are made
                },
                duration: 55,
                ease: 'slow(0.1, 0.4, false)',
            },
            '+=2'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onEnterBack
                journey.enterSideStep(7)
            }
        })
        .to(
            step_7_2_3,
            {
                x: '100vw',
                duration: 20,
            },
            '+=1'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onLeave
                journey.leaveSideStep(7)
            }
        }, '-=1')

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            journey.pinWhileAnimating({
                end: '+=950%',
                pinSpacing: false,
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({ end: '+=950%' })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({ animation: tl_7_2, end: '+=925%' })

    /*** STEP 7.3 ***/
    journey.step = '7-3'
    const tl_7_3 = gsap.timeline()

    tl_7_3
        .fromTo(
            journey.step.querySelector('.bus'),
            {
                scale: 0.9,
                x: 30,
            },
            {
                scale: 1,
                x: 0,
                ease: 'power2.out',
            }
        )
        .to(
            journey.step.querySelector('.layered-image'),
            {
                scale: 1.2,
            },
            '<0.01'
        )

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            // Previous step
            journey.pinWhileAnimating({
                start: 'top center',
                end: '+=950%',
            })
            // Current step
            journey.pinWhileAnimating({
                end: '+=225%',
                pinSpacing: false,
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({ end: '+=225%' })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({ animation: tl_7_3, end: '+=200%' })

    /*** STEP 7.4 ***/
    journey.step = '7-4'
    const tl_7_4 = gsap.timeline()

    tl_7_4
        .to(journey.step.querySelector('.main'), {
            scale: 1.4,
            y: 25,
            ease: 'circ.out',
        })
        .fromTo(
            journey.step.querySelector('.marco'),
            {
                autoAlpha: 0,
                scale: 1.25,
            },
            {
                autoAlpha: 1,
                scale: 1,
            },
            '-=0.5'
        )

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            // Previous step
            journey.pinWhileAnimating({
                start: 'top center',
                end: '+=225%',
            })
            // Current step
            journey.pinWhileAnimating({
                end: '+=225%',
                pinSpacing: false,
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({ end: '+=225%' })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({ animation: tl_7_4, end: '+=200%' })

    /*** STEP 7.5 ***/
    journey.step = '7-5'
    const step_7_5_6 = journey.getStep('7-5_6')
    const tl_7_5 = gsap.timeline()
    const tl_7_5_6 = gsap.timeline()
    journey.addSideStepTimeline(tl_7_5_6)

    tl_7_5
        .from(journey.step.querySelector('.oriol'), {
            scale: 0.9,
            duration: 1,
            ease: 'elastic.out(1, 1)',
        })
        .fromTo(
            journey.step.querySelector('.animated-text'),
            {
                autoAlpha: 0,
                yPercent: -50,
                y: 50,
                scale: 0.9,
            },
            {
                autoAlpha: 1,
                y: 0,
                scale: 1,
                duration: 2,
                ease: 'elastic.out(1, 1)',
            },
            '-=0.5'
        )

    tl_7_5_6
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onLeaveBack
                journey.leaveSideStep(7)
            }
        }, '10')
        .set(step_7_5_6, {
            autoAlpha: 1,
        })
        .fromTo(
            step_7_5_6,
            {
                x: '100vw',
            },
            {
                x: 0,
                duration: 20,
            },
            '+=3'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onEnter
                journey.enterSideStep(7)
            }
        })
        .to(
            step_7_5_6.querySelector('.step-text'),
            {
                y: (index, target, targets) => {
                    return -(vh + target.offsetHeight + 24) // For some reason, the offsetHeight is 24px smaller when the calculations are made
                },
                duration: 55,
                ease: 'slow(0.1, 0.4, false)',
            },
            '+=2'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onEnterBack
                journey.enterSideStep(7)
            }
        })
        .to(
            step_7_5_6,
            {
                x: '100vw',
                duration: 20,
            },
            '+=1'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onLeave
                journey.leaveSideStep(7)
            }
        }, '-=1')

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            // Previous step
            journey.pinWhileAnimating({
                start: 'top center',
                end: '+=225%',
            })
            // Current step
            journey.pinWhileAnimating({
                end: '+=850%',
                pinSpacing: false,
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({ end: '+=850%' })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({
        animation: tl_7_5,
        scrub: false,
        toggleActions: 'play none none reverse',
    })
    journey.triggerAnimation({
        animation: tl_7_5_6,
        end: '+=750%',
    })

    /*** STEP 7.6 ***/
    journey.step = '7-6'
    const tl_7_6 = gsap.timeline()

    tl_7_6
        .fromTo(
            journey.step.querySelector('.tranvia'),
            {
                autoAlpha: 0,
            },
            {
                autoAlpha: 1,
            }
        )
        .fromTo(
            journey.step.querySelector('.autobus'),
            {
                autoAlpha: 0,
            },
            {
                autoAlpha: 1,
            }
        )

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            // Previous step
            journey.pinWhileAnimating({
                start: 'top center',
                end: '+=850%',
            })
            // Current step
            journey.pinWhileAnimating({
                end: '+=150%',
                onLeave: (self) => {
                    journey.leaveStage(7, self)
                },
                onEnterBack: (self) => {
                    journey.enterBackStage(7, self)
                },
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({
                end: '+=150%',
                onLeave: (self) => {
                    journey.leaveStage(7, self)
                },
                onEnterBack: (self) => {
                    journey.enterBackStage(7, self)
                },
            })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({ animation: tl_7_6, end: '+=125%' })
    stageSelectorST = journey.showStageSelector()
    journey.setInitialized(7)
    return true
}

const initializeStage8 = (journey) => {
    /****************
     *** STAGE 8 ***
     *****************/
    journey.stage = 8
    const stage_8 = journey.stage

    /*** STEP 8.1 ***/
    journey.step = '8-1'
    const tl_8_1 = gsap.timeline()

    tl_8_1
        .to(journey.step.querySelector('.stage-introduction'), {
            y: (index, target, targets) => -(vh + target.offsetHeight + 64),
            duration: 50,
            ease: 'slow(0.1, 0.4, false)',
        })
        .fromTo(
            journey.step.querySelector('.fullheight-img'),
            {
                autoAlpha: 1,
            },
            {
                autoAlpha: 0,
                duration: 40,
            }
        )
        .fromTo(
            journey.step.querySelector('.layer.cover'),
            {
                autoAlpha: 0,
            },
            {
                autoAlpha: 1,
                duration: 40,
            },
            '<'
        )
        .fromTo(
            journey.step.querySelector('.layer.cover'),
            {
                scale: 1.15,
            },
            {
                scale: 1.25,
                duration: 20,
            },
            '<'
        )
        .fromTo(
            stage_8.querySelector('.stage-header'),
            {
                autoAlpha: 0,
                y: -50,
            },
            {
                autoAlpha: 1,
                y: 0,
                ease: 'back.out(1.4)',
                duration: 5,
            }
        )
        .fromTo(
            '.side-timeline',
            {
                autoAlpha: 0,
                x: -75,
            },
            {
                autoAlpha: 1,
                x: 0,
                ease: 'back.out(1.4)',
                duration: 5,
            },
            '<'
        )

    journey.pinWhileAnimating({
        end: '+=400%',
        onEnter: () => {
            journey.enterStage(8)
        },
        onLeaveBack: () => {
            journey.leaveBackStage(8)
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({
        animation: tl_8_1,
        start: 'top top+=1px',
        end: '+=375%',
    })

    /*** STEP 8.2 ***/
    journey.step = '8-2'
    const step_8_2_3 = journey.getStep('8-2_3')
    const tl_8_2 = gsap.timeline()
    const tl_8_2_3 = gsap.timeline()
    journey.addSideStepTimeline(tl_8_2_3)

    tl_8_2
        .from(journey.step.querySelector('.oriol'), {
            scale: 0.9,
            duration: 1,
            ease: 'elastic.out(1, 1)',
        })
        .fromTo(
            journey.step.querySelector('.animated-text'),
            {
                autoAlpha: 0,
                yPercent: -50,
                y: 50,
                scale: 0.9,
            },
            {
                autoAlpha: 1,
                y: 0,
                scale: 1,
                duration: 2,
                ease: 'elastic.out(1, 1)',
            },
            '-=0.5'
        )

    tl_8_2_3
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onLeaveBack
                journey.leaveSideStep(8)
            }
        }, '10')
        .set(step_8_2_3, {
            autoAlpha: 1,
        })
        .fromTo(
            step_8_2_3,
            {
                x: '100vw',
            },
            {
                x: 0,
                duration: 20,
            },
            '+=3'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onEnter
                journey.enterSideStep(8, true)
            }
        })
        .to(
            step_8_2_3.querySelector('.step-text'),
            {
                y: (index, target, targets) => {
                    return -(vh + target.offsetHeight + 48) // For some reason, the offsetHeight is 48px smaller when the calculations are made
                },
                duration: 55,
                ease: 'slow(0.1, 0.4, false)',
            },
            '+=2'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onEnterBack
                journey.enterSideStep(8)
            }
        })
        .to(
            step_8_2_3,
            {
                x: '100vw',
                duration: 20,
            },
            '+=1'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onLeave
                journey.leaveSideStep(8)
            }
        }, '-=1')

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            journey.pinWhileAnimating({
                end: '+=850%',
                pinSpacing: false,
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({ end: '+=850%' })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({
        animation: tl_8_2,
        scrub: false,
        toggleActions: 'play none none reverse',
    })
    journey.triggerAnimation({
        animation: tl_8_2_3,
        end: '+=750%',
    })

    /*** STEP 8.3 ***/
    journey.step = '8-3'
    const step_8_3_4 = journey.getStep('8-3_4')
    const tl_8_3 = gsap.timeline()
    journey.addSideStepTimeline(tl_8_3)

    tl_8_3
        .fromTo(
            journey.step.querySelector('.bus'),
            {
                scale: 1.2,
                xPercent: -25,
                y: 60,
            },
            {
                scale: 1,
                xPercent: 0,
                y: 0,
                ease: 'power2.out',
                duration: 15,
            }
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onLeaveBack
                journey.leaveSideStep(8)
            }
        })
        .set(step_8_3_4, {
            autoAlpha: 1,
        })
        .fromTo(
            step_8_3_4,
            {
                x: '100vw',
            },
            {
                x: 0,
                duration: 20,
            },
            '+=3'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onEnter
                journey.enterSideStep(8)
            }
        })
        .to(
            step_8_3_4.querySelector('.step-text'),
            {
                y: (index, target, targets) => {
                    return -(vh + target.offsetHeight + 24) // For some reason, the offsetHeight is 24px smaller when the calculations are made
                },
                duration: 55,
                ease: 'slow(0.1, 0.4, false)',
            },
            '+=2'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onEnterBack
                journey.enterSideStep(8)
            }
        })
        .to(
            step_8_3_4,
            {
                x: '100vw',
                duration: 20,
            },
            '+=1'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onLeave
                journey.leaveSideStep(8)
            }
        }, '-=1')

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            // Previous step
            journey.pinWhileAnimating({
                start: 'top center',
                end: '+=850%',
            })
            // Current step
            journey.pinWhileAnimating({
                end: '+=925%',
                pinSpacing: false,
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({ end: '+=925%' })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({
        animation: tl_8_3,
        end: '+=900%',
    })

    /*** STEP 8.4 ***/
    journey.step = '8-4'
    const step_8_4_5 = journey.getStep('8-4_5')
    const tl_8_4 = gsap.timeline()
    journey.addSideStepTimeline(tl_8_4)

    tl_8_4
        .fromTo(
            journey.step.querySelector('.layered-image'),
            {
                xPercent: -20,
            },
            {
                xPercent: 0,
                duration: 20,
            }
        )
        .fromTo(
            journey.step.querySelector('.bus'),
            {
                x: -40,
                scale: 0.95,
            },
            {
                x: 0,
                scale: 1,
                ease: 'power2.out',
                duration: 20,
            },
            '<'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onLeaveBack
                journey.leaveSideStep(8)
            }
        })
        .set(step_8_4_5, {
            autoAlpha: 1,
        })
        .fromTo(
            step_8_4_5,
            {
                x: '100vw',
            },
            {
                x: 0,
                duration: 20,
            },
            '+=3'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onEnter
                journey.enterSideStep(8)
            }
        })
        .to(
            step_8_4_5.querySelector('.step-text'),
            {
                y: (index, target, targets) => {
                    return -(vh + target.offsetHeight + 24) // For some reason, the offsetHeight is 24px smaller when the calculations are made
                },
                duration: 55,
                ease: 'slow(0.1, 0.4, false)',
            },
            '+=2'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onEnterBack
                journey.enterSideStep(8)
            }
        })
        .to(
            step_8_4_5,
            {
                x: '100vw',
                duration: 20,
            },
            '+=1'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onLeave
                journey.leaveSideStep(8)
            }
        }, '-=1')

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            // Previous step
            journey.pinWhileAnimating({
                start: 'top center',
                end: '+=925%',
            })
            // Current step
            journey.pinWhileAnimating({
                end: '+=950%',
                pinSpacing: false,
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({ end: '+=950%' })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({ animation: tl_8_4, end: '+=925%' })

    /*** STEP 8.5 ***/
    journey.step = '8-5'
    const tl_8_5 = gsap.timeline()

    tl_8_5
        .to(journey.step.querySelector('.layered-image'), {
            scale: 1.25,
        })
        .from(
            journey.step.querySelector('.background'),
            {
                scale: 0.85,
            },
            '<'
        )

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            // Previous step
            journey.pinWhileAnimating({
                start: 'top center',
                end: '+=950%',
            })
            // Current step
            journey.pinWhileAnimating({
                end: '+=250%',
                onLeave: (self) => {
                    journey.leaveStage(8, self)
                },
                onEnterBack: (self) => {
                    journey.enterBackStage(8, self)
                },
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({
                end: '+=250%',
                onLeave: (self) => {
                    journey.leaveStage(8, self)
                },
                onEnterBack: (self) => {
                    journey.enterBackStage(8, self)
                },
            })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({ animation: tl_8_5, end: '+=225%' })
    stageSelectorST = journey.showStageSelector()
    journey.setInitialized(8)
    return true
}

const initializeStage9 = (journey) => {
    /****************
     *** STAGE 9 ***
     *****************/
    journey.stage = 9
    const stage_9 = journey.stage

    /*** STEP 9.1 ***/
    journey.step = '9-1'
    const tl_9_1 = gsap.timeline()

    tl_9_1
        .to(journey.step.querySelector('.stage-introduction'), {
            y: (index, target, targets) => -(vh + target.offsetHeight + 64),
            duration: 50,
            ease: 'slow(0.1, 0.4, false)',
        })
        .fromTo(
            journey.step.querySelector('.fullheight-img'),
            {
                autoAlpha: 1,
            },
            {
                autoAlpha: 0,
                duration: 40,
            }
        )
        .fromTo(
            journey.step.querySelector('.layer.cover'),
            {
                autoAlpha: 0,
            },
            {
                autoAlpha: 1,
                duration: 40,
            },
            '<'
        )
        .fromTo(
            journey.step.querySelector('.layer.cover'),
            {
                scale: 1.15,
            },
            {
                scale: 1.25,
                duration: 20,
            },
            '<'
        )
        .fromTo(
            stage_9.querySelector('.stage-header'),
            {
                autoAlpha: 0,
                y: -50,
            },
            {
                autoAlpha: 1,
                y: 0,
                ease: 'back.out(1.4)',
                duration: 5,
            }
        )
        .fromTo(
            '.side-timeline',
            {
                autoAlpha: 0,
                x: -75,
            },
            {
                autoAlpha: 1,
                x: 0,
                ease: 'back.out(1.4)',
                duration: 5,
            },
            '<'
        )

    journey.pinWhileAnimating({
        end: '+=400%',
        onEnter: () => {
            journey.enterStage(9)
        },
        onLeaveBack: () => {
            journey.leaveBackStage(9)
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({
        animation: tl_9_1,
        start: 'top top+=1px',
        end: '+=375%',
    })

    /*** STEP 9.2 ***/
    journey.step = '9-2'
    const tl_9_2 = gsap.timeline()

    tl_9_2
        .to(journey.step.querySelector('.layered-image'), {
            scale: 1.75,
            x: -150,
            y: 75,
            rotation: 4,
            ease: 'power1.inOut',
            duration: 60,
        })
        .fromTo(
            journey.step.querySelector('.movil'),
            {
                autoAlpha: 1,
            },
            {
                autoAlpha: 0,
                duration: 20,
            },
            '-=15'
        )
        .to(
            journey.step.querySelector('.foto'),
            {
                scale: 1.5,
                xPercent: -8,
                y: 50,
                ease: 'power1.inOut',
                duration: 50,
            },
            '<5'
        )

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            journey.pinWhileAnimating({
                end: '+=300%',
                pinSpacing: false,
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({ end: '+=300%' })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({ animation: tl_9_2, end: '+=275%' })

    /*** STEP 9.3 ***/
    journey.step = '9-3'
    const step_9_3_4 = journey.getStep('9-3_4')
    const tl_9_3 = gsap.timeline()
    journey.addSideStepTimeline(tl_9_3)

    tl_9_3
        .fromTo(
            journey.step.querySelector('.main'),
            {
                scale: 1.5,
                x: -25,
            },
            {
                scale: 1,
                x: 0,
                ease: 'power1.inOut',
                duration: 20,
            }
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onLeaveBack
                journey.leaveSideStep(9)
            }
        })
        .set(step_9_3_4, {
            autoAlpha: 1,
        })
        .fromTo(
            step_9_3_4,
            {
                x: '100vw',
            },
            {
                x: 0,
                duration: 20,
            },
            '+=3'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onEnter
                journey.enterSideStep(9, true)
            }
        })
        .to(
            step_9_3_4.querySelector('.step-text'),
            {
                y: (index, target, targets) => {
                    return -(vh + target.offsetHeight + 24) // For some reason, the offsetHeight is 24px smaller when the calculations are made
                },
                duration: 55,
                ease: 'slow(0.1, 0.4, false)',
            },
            '+=2'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onEnterBack
                journey.enterSideStep(9)
            }
        })
        .to(
            step_9_3_4,
            {
                x: '100vw',
                duration: 20,
            },
            '+=1'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onLeave
                journey.leaveSideStep(9)
            }
        }, '-=1')

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            // Previous step
            journey.pinWhileAnimating({
                start: 'top center',
                end: '+=300%',
            })
            // Current step
            journey.pinWhileAnimating({
                end: '+=950%',
                pinSpacing: false,
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({ end: '+=950%' })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({ animation: tl_9_3, end: '+=925%' })

    /*** STEP 9.4 ***/
    journey.step = '9-4'
    const tl_9_4 = gsap.timeline()

    tl_9_4
        .from(journey.step.querySelector('.laia'), {
            scale: 0.9,
            duration: 1,
            ease: 'elastic.out(1, 1)',
        })
        .fromTo(
            journey.step.querySelector('.animated-text'),
            {
                autoAlpha: 0,
                yPercent: -50,
                y: 50,
                scale: 0.9,
            },
            {
                autoAlpha: 1,
                y: 0,
                scale: 1,
                duration: 2,
                ease: 'elastic.out(1, 1)',
            },
            '-=0.5'
        )

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            // Previous step
            journey.pinWhileAnimating({
                start: 'top center',
                end: '+=950%',
            })
            // Current step
            journey.pinWhileAnimating({
                end: '+=100%',
                pinSpacing: false,
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({ end: '+=100%' })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({
        animation: tl_9_4,
        scrub: false,
        toggleActions: 'play none none reverse',
    })

    /*** STEP 9.5 ***/
    journey.step = '9-5'
    const step_9_5_6 = journey.getStep('9-5_6')
    const tl_9_5 = gsap.timeline()
    journey.addSideStepTimeline(tl_9_5)

    tl_9_5
        .fromTo(
            journey.step.querySelector('.bus'),
            {
                scale: 0.9,
                x: 100,
                y: -35,
            },
            {
                scale: 1,
                x: 0,
                y: 0,
                ease: 'power2.out',
                duration: 15,
            }
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onLeaveBack
                journey.leaveSideStep(9)
            }
        })
        .set(step_9_5_6, {
            autoAlpha: 1,
        })
        .fromTo(
            step_9_5_6,
            {
                x: '100vw',
            },
            {
                x: 0,
                duration: 20,
            },
            '+=3'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onEnter
                journey.enterSideStep(9)
            }
        })
        .to(
            step_9_5_6.querySelector('.step-text'),
            {
                y: (index, target, targets) => {
                    return -(vh + target.offsetHeight + 24) // For some reason, the offsetHeight is 24px smaller when the calculations are made
                },
                duration: 55,
                ease: 'slow(0.1, 0.4, false)',
            },
            '+=2'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onEnterBack
                journey.enterSideStep(9)
            }
        })
        .to(
            step_9_5_6,
            {
                x: '100vw',
                duration: 20,
            },
            '+=1'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onLeave
                journey.leaveSideStep(9)
            }
        }, '-=1')

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            // Previous step
            journey.pinWhileAnimating({ start: 'top center' })
            // Current step
            journey.pinWhileAnimating({
                end: '+=950%',
                pinSpacing: false,
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({ end: '+=950%' })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({ animation: tl_9_5, end: '+=925%' })

    /*** STEP 9.6 ***/
    journey.step = '9-6'
    const tl_9_6 = gsap.timeline()

    tl_9_6.fromTo(
        journey.step.querySelector('.main'),
        {
            xPercent: -30,
        },
        {
            xPercent: 0,
        }
    )

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            // Previous step
            journey.pinWhileAnimating({
                start: 'top center',
                end: '+=950%',
            })
            // Current step
            journey.pinWhileAnimating({
                end: '+=250%',
                onLeave: (self) => {
                    journey.leaveStage(9, self)
                },
                onEnterBack: (self) => {
                    journey.enterBackStage(9, self)
                },
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({
                end: '+=250%',
                onLeave: (self) => {
                    journey.leaveStage(9, self)
                },
                onEnterBack: (self) => {
                    journey.enterBackStage(9, self)
                },
            })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({ animation: tl_9_6, end: '+=225%' })
    stageSelectorST = journey.showStageSelector()
    journey.setInitialized(9)
    return true
}

const initializeStage10 = (journey) => {
    /****************
     *** STAGE 10 ***
     *****************/
    journey.stage = 10
    const stage_10 = journey.stage

    /*** STEP 10.1 ***/
    journey.step = '10-1'
    const tl_10_1 = gsap.timeline()

    tl_10_1
        .to(journey.step.querySelector('.stage-introduction'), {
            y: (index, target, targets) => -(vh + target.offsetHeight + 64),
            duration: 50,
            ease: 'slow(0.1, 0.4, false)',
        })
        .to(journey.step.querySelector('.step-text'), {
            y: (index, target, targets) => -(vh + target.offsetHeight + 64),
            duration: 50,
            ease: 'slow(0.1, 0.4, false)',
        })
        .fromTo(
            journey.step.querySelector('.fullheight-img'),
            {
                autoAlpha: 1,
            },
            {
                autoAlpha: 0,
                duration: 40,
            }
        )
        .fromTo(
            journey.step.querySelector('.layer.cover'),
            {
                autoAlpha: 0,
            },
            {
                autoAlpha: 1,
                duration: 40,
            },
            '<'
        )
        .fromTo(
            journey.step.querySelector('.layer.cover'),
            {
                scale: 1,
            },
            {
                scale: 1.05,
                duration: 20,
            },
            '<'
        )
        .fromTo(
            stage_10.querySelector('.stage-header'),
            {
                autoAlpha: 0,
                y: -50,
            },
            {
                autoAlpha: 1,
                y: 0,
                ease: 'back.out(1.4)',
                duration: 5,
            }
        )
        .fromTo(
            '.side-timeline',
            {
                autoAlpha: 0,
                x: -75,
            },
            {
                autoAlpha: 1,
                x: 0,
                ease: 'back.out(1.4)',
                duration: 5,
            },
            '<'
        )

    journey.pinWhileAnimating({
        end: '+=725%',
        onEnter: () => {
            journey.enterStage(10)
        },
        onLeaveBack: () => {
            journey.leaveBackStage(10)
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({
        animation: tl_10_1,
        start: 'top top+=1px',
        end: '+=700%',
    })

    /*** STEP 10.2 ***/
    journey.step = '10-2'
    const tl_10_2 = gsap.timeline()
    const tl_stop = gsap.timeline({ repeat: -1, repeatDelay: 1 })

    tl_stop.fromTo(
        journey.step.querySelector('.parada'),
        {
            autoAlpha: 1,
        },
        {
            autoAlpha: 0,
            duration: 0.001,
            repeat: 1,
            yoyo: true,
            repeatDelay: 0.4,
        }
    )

    tl_10_2.fromTo(
        journey.step.querySelector('.layered-image'),
        {
            xPercent: 75,
            skewX: -12,
        },
        {
            xPercent: 0,
            skewX: 0,
            ease: 'back.out(1.4)',
        }
    )

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            journey.pinWhileAnimating({ pinSpacing: false })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating()
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({
        animation: tl_stop,
        end: null,
        scrub: false,
    })
    journey.triggerAnimation({ animation: tl_10_2, end: '+=75%' })

    /*** STEP 10.3 ***/
    journey.step = '10-3'
    const step_10_3_4 = journey.getStep('10-3_4')
    const tl_10_3 = gsap.timeline()
    journey.addSideStepTimeline(tl_10_3)

    tl_10_3
        .fromTo(
            journey.step.querySelector('.main'),
            {
                xPercent: -30,
            },
            {
                xPercent: 0,
                duration: 15,
            }
        )
        .to(journey.step.querySelector('.main'), {
            scale: 1.5,
            xPercent: 8,
            y: 50,
            duration: 15,
        })
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onLeaveBack
                journey.leaveSideStep(10)
            }
        })
        .set(step_10_3_4, {
            autoAlpha: 1,
        })
        .fromTo(
            step_10_3_4,
            {
                x: '100vw',
            },
            {
                x: 0,
                duration: 20,
            },
            '+=3'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onEnter
                journey.enterSideStep(10, true)
            }
        })
        .to(
            step_10_3_4.querySelector('.step-text'),
            {
                y: (index, target, targets) => {
                    return -(vh + target.offsetHeight + 24) // For some reason, the offsetHeight is 24px smaller when the calculations are made
                },
                duration: 55,
                ease: 'slow(0.1, 0.4, false)',
            },
            '+=2'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onEnterBack
                journey.enterSideStep(10)
            }
        })
        .to(
            step_10_3_4,
            {
                x: '100vw',
                duration: 20,
            },
            '+=1'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onLeave
                journey.leaveSideStep(10)
            }
        }, '-=1')

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            // Previous step
            journey.pinWhileAnimating({ start: 'top center' })
            // Current step
            journey.pinWhileAnimating({
                end: '+=950%',
                pinSpacing: false,
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({ end: '+=950%' })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({ animation: tl_10_3, end: '+=925%' })

    /*** STEP 10.4 ***/
    journey.step = '10-4'
    const step_10_4_5 = journey.getStep('10-4_5')
    const tl_10_4 = gsap.timeline()
    journey.addSideStepTimeline(tl_10_4)

    tl_10_4
        .to(journey.step.querySelector('.layered-image'), {
            scale: 1.5,
            x: -150,
            duration: 8,
        })
        .fromTo(
            journey.step.querySelector('.bus'),
            {
                x: -75,
                y: 15,
                scale: 1.05,
            },
            {
                x: 0,
                y: 0,
                scale: 1,
                duration: 10,
            },
            '<'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onLeaveBack
                journey.leaveSideStep(10)
            }
        })
        .set(step_10_4_5, {
            autoAlpha: 1,
        })
        .fromTo(
            step_10_4_5,
            {
                x: '100vw',
            },
            {
                x: 0,
                duration: 20,
            },
            '+=3'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onEnter
                journey.enterSideStep(10)
            }
        })
        .to(
            step_10_4_5.querySelector('.step-text'),
            {
                y: (index, target, targets) => {
                    return -(vh + target.offsetHeight + 24) // For some reason, the offsetHeight is 24px smaller when the calculations are made
                },
                duration: 55,
                ease: 'slow(0.1, 0.4, false)',
            },
            '+=2'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onEnterBack
                journey.enterSideStep(10)
            }
        })
        .to(
            step_10_4_5,
            {
                x: '100vw',
                duration: 20,
            },
            '+=1'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onLeave
                journey.leaveSideStep(10)
            }
        }, '-=1')

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            // Previous step
            journey.pinWhileAnimating({
                start: 'top center',
                end: '+=950%',
            })
            // Current step
            journey.pinWhileAnimating({
                end: '+=850%',
                pinSpacing: false,
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({ end: '+=850%' })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({ animation: tl_10_4, end: '+=825%' })

    /*** STEP 10.5 ***/
    journey.step = '10-5'
    const tl_10_5 = gsap.timeline()

    tl_10_5
        .from(journey.step.querySelector('.laia'), {
            scale: 0.9,
            duration: 1,
            ease: 'elastic.out(1, 1)',
        })
        .fromTo(
            journey.step.querySelector('.animated-text'),
            {
                autoAlpha: 0,
                yPercent: -50,
                y: 50,
                scale: 0.9,
            },
            {
                autoAlpha: 1,
                y: 0,
                scale: 1,
                duration: 2,
                ease: 'elastic.out(1, 1)',
            },
            '-=0.5'
        )

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            // Previous step
            journey.pinWhileAnimating({
                start: 'top center',
                end: '+=850%',
            })
            // Current step
            journey.pinWhileAnimating({ pinSpacing: false })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating()
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({
        animation: tl_10_5,
        scrub: false,
        toggleActions: 'play none none reverse',
    })

    /*** STEP 10.6 ***/
    journey.step = '10-6'
    const tl_10_6 = gsap.timeline()

    tl_10_6
        .from(journey.step.querySelector('.pepa'), {
            scale: 0.9,
            duration: 1,
            ease: 'elastic.out(1, 1)',
        })
        .fromTo(
            journey.step.querySelector('.animated-text'),
            {
                autoAlpha: 0,
                yPercent: -50,
                y: 50,
                scale: 0.9,
            },
            {
                autoAlpha: 1,
                y: 0,
                scale: 1,
                duration: 2,
                ease: 'elastic.out(1, 1)',
            },
            '-=0.5'
        )

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            // Previous step
            journey.pinWhileAnimating({ start: 'top center' })
            // Current step
            journey.pinWhileAnimating({ pinSpacing: false })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating()
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({
        animation: tl_10_6,
        scrub: false,
        toggleActions: 'play none none reverse',
    })

    /*** STEP 10.7 ***/
    journey.step = '10-7'
    const tl_10_7 = gsap.timeline()

    tl_10_7.fromTo(
        journey.step.querySelector('.main'),
        {
            scale: 2,
        },
        {
            scale: 1,
        }
    )

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            // Previous step
            journey.pinWhileAnimating({ start: 'top center' })
            // Current step
            journey.pinWhileAnimating({
                end: '+=425%',
                onLeave: (self) => {
                    journey.leaveStage(10, self)
                },
                onEnterBack: (self) => {
                    journey.enterBackStage(10, self)
                },
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({
                end: '+=425%',
                onLeave: (self) => {
                    journey.leaveStage(10, self)
                },
                onEnterBack: (self) => {
                    journey.enterBackStage(10, self)
                },
            })
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({ animation: tl_10_7, end: '+=375%' })
    stageSelectorST = journey.showStageSelector()
    journey.setInitialized(10)
    return true
}

const initializeStage11 = (journey) => {
    /****************
     *** STAGE 11 ***
     *****************/
    journey.stage = 11
    const stage_11 = journey.stage

    /*** STEP 11.1 ***/
    journey.step = '11-1'
    const tl_11_1 = gsap.timeline()
    const tl_play_btn = gsap.timeline()
    const video_11_1 = journey.step.querySelector('#step-11-1-video')
    const play_button_11_1 = journey.step.querySelector('.play-button')
    const step_11_1_2_1 = journey.getStep('11-1_2_1')
    const step_11_1_2_2 = journey.getStep('11-1_2_2')
    const step_11_1_2_3 = journey.getStep('11-1_2_3')
    journey.addSideStepTimeline(tl_11_1)

    video_11_1.addEventListener('ended', () => {
        $(play_button_11_1).parent('.btn-wrapper').show()
        gsap.to(play_button_11_1, {
            autoAlpha: 1,
        })
    })

    tl_play_btn
        .fromTo(
            play_button_11_1,
            {
                autoAlpha: 0,
                scale: 0.5,
            },
            {
                autoAlpha: 1,
                scale: 0.85,
            }
        )
        .to(play_button_11_1, {
            scale: 1,
            duration: 1,
            repeat: -1,
            yoyo: true,
        })

    tl_11_1
        .to(journey.step.querySelector('.stage-introduction'), {
            y: (index, target, targets) => -(vh + target.offsetHeight + 64),
            duration: 50,
            ease: 'slow(0.1, 0.4, false)',
        })
        .fromTo(
            stage_11.querySelector('.stage-header'),
            {
                autoAlpha: 0,
                y: -50,
            },
            {
                autoAlpha: 1,
                y: 0,
                ease: 'back.out(1.4)',
                duration: 5,
            }
        )
        .fromTo(
            '.side-timeline',
            {
                autoAlpha: 0,
                x: -75,
            },
            {
                autoAlpha: 1,
                x: 0,
                ease: 'back.out(1.4)',
                duration: 5,
            },
            '<'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onLeaveBack
                journey.leaveSideStep(11)
            }
        })
        .set(step_11_1_2_1, {
            autoAlpha: 1,
        })
        .fromTo(
            step_11_1_2_1,
            {
                x: '100vw',
            },
            {
                x: 0,
                duration: 20,
            },
            '+=5'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onEnter
                journey.enterSideStep(11, true)
            }
        })
        .to(
            step_11_1_2_1.querySelector('.step-text'),
            {
                y: (index, target, targets) => {
                    return -(vh + target.offsetHeight + 24) // For some reason, the offsetHeight is 24px smaller when the calculations are made
                },
                duration: 55,
                ease: 'slow(0.1, 0.4, false)',
            },
            '+=2'
        )
        .set(step_11_1_2_2, {
            autoAlpha: 1,
        })
        .fromTo(
            step_11_1_2_2,
            {
                x: '100vw',
            },
            {
                x: 0,
                duration: 20,
            }
        )
        .to(
            step_11_1_2_2.querySelector('.step-text'),
            {
                y: (index, target, targets) => {
                    return -(vh + target.offsetHeight + 24) // For some reason, the offsetHeight is 24px smaller when the calculations are made
                },
                duration: 55,
                ease: 'slow(0.1, 0.4, false)',
            },
            '+=2'
        )
        .set(step_11_1_2_3, {
            autoAlpha: 1,
        })
        .fromTo(
            step_11_1_2_3,
            {
                x: '100vw',
            },
            {
                x: 0,
                duration: 20,
            }
        )
        .to(
            step_11_1_2_3.querySelector('.step-text'),
            {
                y: (index, target, targets) => {
                    return -(vh + target.offsetHeight + 24) // For some reason, the offsetHeight is 24px smaller when the calculations are made
                },
                duration: 55,
                ease: 'slow(0.1, 0.4, false)',
            },
            '+=2'
        )
        .add(function () {
            if (this.parent.scrollTrigger.direction === -1) {
                // onEnterBack
                journey.enterSideStep(11)
            }
        })
        .add(function () {
            if (this.parent.scrollTrigger.direction === 1) {
                // onLeave
                journey.leaveSideStep(11)
            }
        }, '-=2')

    journey.pinWhileAnimating({
        end: '+=1600%',
        onEnter: () => {
            journey.enterStage(11)
        },
        onLeaveBack: () => {
            journey.leaveBackStage(11)
        },
    })
    journey.pinWhileTransitioning()
    journey.triggerAnimation({
        animation: tl_play_btn,
        start: 'top top+=1px',
        end: null,
        scrub: false,
    })
    journey.triggerAnimation({
        animation: tl_11_1,
        start: 'top top+=1px',
        end: '+=1500%',
    })

    /*** STEP 11.2 ***/
    journey.step = '11-2'
    const tl_11_2 = gsap.timeline()

    tl_11_2
        .fromTo(
            journey.step.querySelector('.linia'),
            {
                y: 50,
                scaleY: 0.01,
                transformOrigin: 'bottom',
            },
            {
                y: 0,
                scaleY: 1,
            }
        )
        .fromTo(
            journey.step.querySelector('.background'),
            {
                autoAlpha: 0,
            },
            {
                autoAlpha: 1,
            },
            '<'
        )

    journey.pinWhileAnimatingResponsive({
        '(max-width: 575.98px)': function () {
            journey.pinWhileAnimating({
                end: '+=350%',
                onLeave: (self) => {
                    journey.leaveStage(11, self)
                },
                onEnterBack: (self) => {
                    journey.enterBackStage(11, self)
                },
            })
        },
        '(min-width: 576px)': function () {
            journey.pinWhileAnimating({
                end: '+=350%',
                onLeave: (self) => {
                    journey.leaveStage(11, self)
                },
                onEnterBack: (self) => {
                    journey.enterBackStage(11, self)
                },
            })
        },
    })

    journey.pinWhileTransitioning()
    journey.triggerAnimation({ animation: tl_11_2, end: '+=275%' })
    stageSelectorST = journey.showStageSelector(null, false)

    /*** Related pages section ***/

    ScrollTrigger.matchMedia({
        '(min-width: 768px)': function () {
            relatedPagesST = journey.pinWhileAnimating({
                trigger: '#related-pages',
                end: '+=200',
            })
        },
    })

    gsap.set('.stage-header', {
        autoAlpha: 0,
        y: -50,
    })

    gsap.set('.side-step', {
        autoAlpha: 0,
    })

    journey.setInitialized(11)
    return true
}
